import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowClockwise, ArrowSquareOut, Plus } from 'shared/icons';
import { fmtDate, SUPPLIER_OUTCOME_LABELS, timeAgo } from '../components/rma/rmaShared';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

/**
 * Garantia — o RMA com o FABRICANTE (2026-08-25), separado do Pós-venda.
 *
 * Visão principal: o LOTE — o registro de RMA "em si". Abre-se um RMA,
 * incluem-se vários produtos, anexa-se a NF de devolução no despacho (fica
 * AGUARDANDO) e o desfecho é dado por item (reembolso, crédito, reposição,
 * conserto, descarte, sem retorno); o lote conclui quando o último item
 * fechar. A visão POR ITEM continua disponível (mesmos dados por baixo).
 */

const BATCH_STATUS_META = {
    ABERTO: {
        label: 'Aberto (montando)',
        pill: 'bg-sky-50 text-sky-700 ring-sky-600/25 dark:bg-sky-400/10 dark:text-sky-300',
    },
    AGUARDANDO: {
        label: 'Aguardando fabricante',
        pill: 'bg-warning-50 text-warning-800 ring-warning-600/25 dark:bg-warning-400/10 dark:text-warning-300',
    },
    CONCLUIDO: {
        label: 'Concluído',
        pill: 'bg-emerald-50 text-emerald-700 ring-emerald-600/25 dark:bg-emerald-400/10 dark:text-emerald-300',
    },
};

const STAGES = [
    {
        key: 'A_CADASTRAR',
        label: 'A cadastrar',
        taskKey: 'enviar_fabricante',
        hint: 'Avaria confirmada na inspeção — incluir num RMA ou abrir no sistema do fabricante.',
    },
    {
        key: 'CADASTRADO',
        label: 'Despachar',
        taskKey: 'fabricante_despachar',
        hint: 'Cadastrado no fabricante — emitir a NF de devolução e enviar a mercadoria.',
    },
    {
        key: 'DESPACHADO',
        label: 'No fabricante',
        taskKey: 'fabricante_retorno',
        hint: 'Mercadoria enviada — aguardando o retorno do fornecedor.',
    },
    {
        key: 'CONCLUIDO',
        label: 'Concluídos',
        taskKey: null,
        hint: 'Trilho encerrado, com o desfecho dado pelo fornecedor.',
    },
];

const OUTCOME_TONES = {
    CREDITO: 'bg-emerald-50 text-emerald-700 ring-emerald-600/25 dark:bg-emerald-400/10 dark:text-emerald-300',
    REEMBOLSO: 'bg-emerald-50 text-emerald-700 ring-emerald-600/25 dark:bg-emerald-400/10 dark:text-emerald-300',
    REPOSICAO: 'bg-sky-50 text-sky-700 ring-sky-600/25 dark:bg-sky-400/10 dark:text-sky-300',
    CONSERTO: 'bg-sky-50 text-sky-700 ring-sky-600/25 dark:bg-sky-400/10 dark:text-sky-300',
    DESCARTE: 'bg-mono-100 text-mono-500 ring-mono-400/20 dark:bg-mono-900 dark:text-mono-400',
    SEM_RETORNO: 'bg-danger-50 text-danger-700 ring-danger-600/25 dark:bg-danger-400/10 dark:text-danger-300',
};

function daysSince(iso) {
    if (!iso) {
        return null;
    }
    return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

// Data-referência do estágio atual (para o "há Xd" da linha por item).
function stageDate(rma) {
    return rma.supplier_concluded_at || rma.supplier_shipped_at || rma.supplier_registered_at || rma.inspected_at;
}

const OutcomeChip = ({ outcome }) => (
    <span
        className={`inline-flex px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${OUTCOME_TONES[outcome] || OUTCOME_TONES.DESCARTE}`}
    >
        {SUPPLIER_OUTCOME_LABELS[outcome] || outcome}
    </span>
);

// Tile do resumo fiscal: pendente em evidência, feito como contexto.
const FiscalTile = ({ label, pending, done, doneLabel }) => (
    <div className="flex flex-col gap-0.5 border border-mono-200 bg-white px-4 py-3 dark:border-mono-800 dark:bg-mono-950">
        <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">{label}</p>
        <p
            className={`text-2xl font-bold tabular-nums ${
                pending > 0 ? 'text-warning-text dark:text-warning-300' : 'text-black dark:text-white'
            }`}
        >
            {pending}
            <span className="ml-1.5 text-xs font-semibold text-mono-400 dark:text-mono-600">a emitir</span>
        </p>
        <p className="text-xs text-mono-500 dark:text-mono-400">
            {done} {doneLabel}
        </p>
    </div>
);

const CreateBatchModal = ({ isOpen, onClose, onConfirm, saving }) => {
    const [supplierName, setSupplierName] = useState('');
    const [note, setNote] = useState('');
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Abrir RMA ao fornecedor" size="sm">
            <div className="flex flex-col gap-3">
                <Input
                    label="Fabricante / fornecedor"
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="ex.: Gran Plus"
                />
                <Textarea
                    label="Observação (opcional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        loading={saving}
                        disabled={!supplierName.trim()}
                        onClick={() => onConfirm({ supplier_name: supplierName.trim(), note })}
                    >
                        Abrir RMA
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// Seleção de produtos elegíveis (desfecho fornecedor/garantia, sem lote).
const AddItemsModal = ({ isOpen, onClose, onConfirm, saving }) => {
    const [q, setQ] = useState('');
    const [candidates, setCandidates] = useState(null);
    const [selected, setSelected] = useState(new Set());

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        let alive = true;
        api.listSupplierBatchCandidates(q)
            .then((data) => alive && setCandidates(data.rmas || []))
            .catch(() => alive && setCandidates([]));
        return () => {
            alive = false;
        };
    }, [isOpen, q]);

    const toggle = (id) =>
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Incluir produtos no RMA" size="md">
            <div className="flex flex-col gap-3">
                <Input
                    placeholder="Buscar por SKU, produto ou pedido…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <div className="max-h-80 overflow-y-auto border border-mono-200 dark:border-mono-800">
                    {candidates === null ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner />
                        </div>
                    ) : candidates.length === 0 ? (
                        <p className="py-8 text-center text-xs text-mono-400">
                            Nenhum produto elegível (desfecho fornecedor/garantia, ainda sem RMA).
                        </p>
                    ) : (
                        candidates.map((rma) => (
                            <label
                                key={rma.id}
                                className="flex cursor-pointer items-center gap-3 border-b border-mono-100 px-4 py-2.5 last:border-b-0 hover:bg-mono-50 dark:border-mono-900 dark:hover:bg-mono-900/40"
                            >
                                <input type="checkbox" checked={selected.has(rma.id)} onChange={() => toggle(rma.id)} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium text-black dark:text-white">
                                        {rma.item_title || rma.sku || rma.id.slice(-8)}
                                    </span>
                                    <span className="block truncate font-mono text-[11px] text-mono-500 dark:text-mono-400">
                                        {[
                                            rma.sku,
                                            rma.quantity > 1 ? `${rma.quantity} un` : null,
                                            rma.serial_number ? `série ${rma.serial_number}` : null,
                                            rma.external_order_id,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                </span>
                            </label>
                        ))
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} disabled={selected.size === 0} onClick={() => onConfirm([...selected])}>
                        Incluir {selected.size || ''} produto(s)
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

const ShipBatchModal = ({ isOpen, onClose, onConfirm, saving, batch }) => {
    const [nfNumber, setNfNumber] = useState('');
    const [note, setNote] = useState('');
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Despachar ${batch?.code || 'RMA'}`} size="sm">
            <div className="flex flex-col gap-3">
                <p className="text-xs text-mono-500 dark:text-mono-400">
                    {batch?.items_count} produto(s) para {batch?.supplier_name}. Registre a NF de devolução desta
                    remessa — o RMA fica <strong>aguardando o fabricante</strong>.
                </p>
                <Input
                    label="NF de devolução (número)"
                    value={nfNumber}
                    onChange={(e) => setNfNumber(e.target.value)}
                    placeholder="ex.: 12345"
                />
                <Textarea
                    label="Observação (opcional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} onClick={() => onConfirm({ nf_number: nfNumber.trim(), note })}>
                        Confirmar despacho
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// Desfecho de um item do lote — ou de todos os pendentes de uma vez.
const OutcomeModal = ({ isOpen, onClose, onConfirm, saving, title, description }) => {
    const [outcome, setOutcome] = useState('CREDITO');
    const [note, setNote] = useState('');
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
            <div className="flex flex-col gap-3">
                {description && <p className="text-xs text-mono-500 dark:text-mono-400">{description}</p>}
                <Select label="Desfecho" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                    {Object.entries(SUPPLIER_OUTCOME_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </Select>
                <Textarea
                    label="Observação (opcional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                />
                <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button loading={saving} onClick={() => onConfirm({ outcome, note })}>
                        Confirmar
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

const Garantia = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canAct = checkPermission('rmas:mediation:update') || checkPermission('rmas:resolve');

    const [view, setView] = useState('lotes'); // 'lotes' | 'itens'
    const [tasks, setTasks] = useState(null);
    const [busy, setBusy] = useState(false);

    // — Visão lotes —
    const [batches, setBatches] = useState(null);
    const [batchDetail, setBatchDetail] = useState(null); // {batch, items}
    const [createOpen, setCreateOpen] = useState(false);
    const [addItemsOpen, setAddItemsOpen] = useState(false);
    const [shipOpen, setShipOpen] = useState(false);
    const [outcomeTarget, setOutcomeTarget] = useState(null); // {rmaId?|all}

    // — Visão por item —
    const [stage, setStage] = useState('A_CADASTRAR');
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(
        async ({ silent = false } = {}) => {
            if (!silent) {
                setLoading(true);
            }
            try {
                const [tasksData, batchesData, listData] = await Promise.all([
                    api.getRmaTasks({}),
                    api.listSupplierBatches(),
                    view === 'itens' ? api.getRmas({ supplier_stage: stage, limit: 100 }) : Promise.resolve(null),
                ]);
                setTasks(tasksData);
                setBatches(batchesData.batches || []);
                if (listData) {
                    setList(listData);
                }
            } catch (err) {
                if (!silent) {
                    toast.error(err.message || 'Não foi possível carregar a garantia.');
                }
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stage, view],
    );

    useEffect(() => {
        load();
    }, [load]);

    const openBatch = async (id) => {
        try {
            setBatchDetail(await api.getSupplierBatch(id));
        } catch (err) {
            toast.error(err.message || 'Não foi possível abrir o RMA.');
        }
    };

    const act = async (fn, doneMessage) => {
        setBusy(true);
        try {
            const result = await fn();
            toast.success(doneMessage);
            setCreateOpen(false);
            setAddItemsOpen(false);
            setShipOpen(false);
            setOutcomeTarget(null);
            // Ações de lote devolvem o detalhe atualizado — mantém o painel aberto.
            if (result?.batch) {
                setBatchDetail(result);
            }
            await load({ silent: true });
        } catch (err) {
            toast.error(err.message || 'Não foi possível concluir a ação.');
        } finally {
            setBusy(false);
        }
    };

    const stageCount = (s) => {
        if (s.key === 'CONCLUIDO') {
            return stage === 'CONCLUIDO' && list ? list.total : null;
        }
        return tasks?.atendimento?.[s.taskKey] ?? null;
    };

    const activeStage = STAGES.find((s) => s.key === stage);
    const rows = list?.rmas || [];
    const detailBatch = batchDetail?.batch || null;
    const detailItems = batchDetail?.items || [];
    const pendingItems = detailItems.filter((i) => !i.supplier_concluded_at);

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold text-black dark:text-white">Garantia</h1>
                    <p className="text-sm text-mono-500 dark:text-mono-400">
                        RMA com o fabricante — lotes, notas de devolução e retornos.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {canAct && (
                        <Button size="sm" onClick={() => setCreateOpen(true)}>
                            <Plus size={16} className="mr-1" /> Abrir RMA
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => load()}>
                        <ArrowClockwise size={16} />
                    </Button>
                </div>
            </div>

            {/* Resumo fiscal: o pendente e o já feito de cada nota */}
            {tasks?.fiscal && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <FiscalTile
                        label="NF de perda"
                        pending={tasks.fiscal.nf_perda_pendente}
                        done={tasks.fiscal.nf_perda_emitidas}
                        doneLabel="emitidas"
                    />
                    <FiscalTile
                        label="NF de entrada"
                        pending={tasks.fiscal.nf_entrada_pendente}
                        done={tasks.fiscal.nf_entrada_emitidas}
                        doneLabel="emitidas"
                    />
                    <FiscalTile
                        label="NF de devolução (fornecedor)"
                        pending={tasks.fiscal.despachados_sem_nf}
                        done={tasks.fiscal.nf_devolucao_enviadas}
                        doneLabel="enviadas"
                    />
                </div>
            )}

            {/* Alternância lote × item */}
            <div className="flex gap-2">
                {[
                    ['lotes', 'RMAs ao fornecedor'],
                    ['itens', 'Por item'],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        onClick={() => setView(key)}
                        className={` px-4 py-1.5 text-sm font-semibold transition-colors ${
                            view === key
                                ? 'bg-black text-white dark:bg-white dark:text-black'
                                : 'bg-white text-mono-700 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : view === 'lotes' ? (
                <div className="flex flex-col gap-3">
                    {(batches || []).length === 0 && (
                        <p className=" border border-dashed border-mono-200 py-16 text-center text-sm text-mono-400 dark:border-mono-800">
                            Nenhum RMA aberto ainda — clique em “Abrir RMA” e inclua os produtos com avaria.
                        </p>
                    )}
                    {(batches || []).map((batch) => {
                        const meta = BATCH_STATUS_META[batch.status] || BATCH_STATUS_META.ABERTO;
                        const isOpenDetail = detailBatch?.id === batch.id;
                        return (
                            <div
                                key={batch.id}
                                className=" border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950"
                            >
                                <button
                                    type="button"
                                    onClick={() => (isOpenDetail ? setBatchDetail(null) : openBatch(batch.id))}
                                    className="flex w-full flex-wrap items-center justify-between gap-2 px-5 py-3.5 text-left"
                                >
                                    <span className="flex min-w-0 items-center gap-3">
                                        <span className="font-mono text-sm font-bold text-black dark:text-white">
                                            {batch.code}
                                        </span>
                                        <span className="truncate text-sm font-medium text-mono-700 dark:text-mono-300">
                                            {batch.supplier_name}
                                        </span>
                                        <span
                                            className={`inline-flex px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${meta.pill}`}
                                        >
                                            {meta.label}
                                        </span>
                                    </span>
                                    <span className="flex shrink-0 items-center gap-4 text-xs text-mono-500 dark:text-mono-400">
                                        <span>
                                            {batch.items_count} produto(s)
                                            {batch.status !== 'ABERTO'
                                                ? ` · ${batch.items_concluded}/${batch.items_count} com desfecho`
                                                : ''}
                                        </span>
                                        {batch.nf_number ? (
                                            <span className="font-mono">NF {batch.nf_number}</span>
                                        ) : batch.status !== 'ABERTO' ? (
                                            <span className="text-warning-text dark:text-warning-300">sem NF</span>
                                        ) : null}
                                        <span>{timeAgo(batch.shipped_at || batch.created_at)}</span>
                                    </span>
                                </button>

                                {isOpenDetail && (
                                    <div className="flex flex-col gap-3 border-t border-mono-100 px-5 py-4 dark:border-mono-900">
                                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-mono-500 dark:text-mono-400">
                                            <span>aberto {fmtDate(detailBatch.created_at)}</span>
                                            {detailBatch.shipped_at && (
                                                <span>despachado {fmtDate(detailBatch.shipped_at)}</span>
                                            )}
                                            {detailBatch.nf_number && (
                                                <span className="font-mono">
                                                    NF de devolução {detailBatch.nf_number}
                                                    {detailBatch.nf_at ? ` · ${fmtDate(detailBatch.nf_at)}` : ''}
                                                </span>
                                            )}
                                            {detailBatch.concluded_at && (
                                                <span>concluído {fmtDate(detailBatch.concluded_at)}</span>
                                            )}
                                            {detailBatch.note && <span>“{detailBatch.note}”</span>}
                                        </div>

                                        {detailItems.map((item) => (
                                            <div
                                                key={item.id}
                                                className="flex flex-wrap items-center justify-between gap-2 bg-mono-50 px-4 py-2.5 dark:bg-mono-900/50"
                                            >
                                                <span className="min-w-0 flex-1">
                                                    <span className="block truncate text-sm font-medium text-black dark:text-white">
                                                        {item.item_title || item.sku || item.id.slice(-8)}
                                                    </span>
                                                    <span className="block truncate font-mono text-[11px] text-mono-500 dark:text-mono-400">
                                                        {[
                                                            item.sku,
                                                            item.quantity > 1 ? `${item.quantity} un` : null,
                                                            item.serial_number ? `série ${item.serial_number}` : null,
                                                            item.external_order_id,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </span>
                                                </span>
                                                <span className="flex shrink-0 items-center gap-2">
                                                    {item.supplier_outcome ? (
                                                        <OutcomeChip outcome={item.supplier_outcome} />
                                                    ) : detailBatch.status === 'AGUARDANDO' && canAct ? (
                                                        <Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => setOutcomeTarget({ rmaId: item.id })}
                                                        >
                                                            Desfecho
                                                        </Button>
                                                    ) : (
                                                        <span className="text-[11px] text-mono-400">aguardando</span>
                                                    )}
                                                    {detailBatch.status === 'ABERTO' && canAct && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                act(
                                                                    () =>
                                                                        api.removeSupplierBatchItem(
                                                                            detailBatch.id,
                                                                            item.id,
                                                                        ),
                                                                    'Produto removido do RMA.',
                                                                )
                                                            }
                                                            className="text-[11px] font-semibold text-mono-400 underline underline-offset-2 hover:text-danger-600"
                                                        >
                                                            remover
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/rmas/${item.id}`)}
                                                        className="text-mono-400 hover:text-black dark:hover:text-white"
                                                        title="abrir ocorrência"
                                                    >
                                                        <ArrowSquareOut size={14} />
                                                    </button>
                                                </span>
                                            </div>
                                        ))}

                                        {canAct && (
                                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                                {detailBatch.status === 'ABERTO' && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => setAddItemsOpen(true)}
                                                        >
                                                            <Plus size={14} className="mr-1" /> Incluir produtos
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            disabled={detailItems.length === 0}
                                                            onClick={() => setShipOpen(true)}
                                                        >
                                                            Despachar (com NF)
                                                        </Button>
                                                    </>
                                                )}
                                                {detailBatch.status === 'AGUARDANDO' && pendingItems.length > 1 && (
                                                    <Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => setOutcomeTarget({ all: true })}
                                                    >
                                                        Desfecho para todos ({pendingItems.length})
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap gap-2">
                        {STAGES.map((s) => {
                            const count = stageCount(s);
                            const active = stage === s.key;
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => setStage(s.key)}
                                    className={`inline-flex items-center gap-2 px-4 py-1.5 text-sm font-semibold transition-colors ${
                                        active
                                            ? 'bg-black text-white dark:bg-white dark:text-black'
                                            : 'bg-white text-mono-700 ring-1 ring-inset ring-mono-200 hover:bg-mono-100 dark:bg-mono-950 dark:text-mono-300 dark:ring-mono-800 dark:hover:bg-mono-900'
                                    }`}
                                >
                                    {s.label}
                                    {count !== null && (
                                        <span
                                            className={` px-1.5 text-[11px] font-bold tabular-nums ${
                                                active
                                                    ? 'bg-white/20 dark:bg-black/15'
                                                    : 'bg-mono-100 text-mono-500 dark:bg-mono-900 dark:text-mono-400'
                                            }`}
                                        >
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {activeStage && <p className="text-xs text-mono-400 dark:text-mono-600">{activeStage.hint}</p>}

                    {rows.length === 0 ? (
                        <p className=" border border-dashed border-mono-200 py-16 text-center text-sm text-mono-400 dark:border-mono-800">
                            Nada neste estágio agora.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {rows.map((rma) => {
                                const days = daysSince(stageDate(rma));
                                return (
                                    <div
                                        key={rma.id}
                                        className="flex flex-col gap-2 border border-mono-200 bg-white px-5 py-3.5 dark:border-mono-800 dark:bg-mono-950"
                                    >
                                        <div className="flex w-full flex-wrap items-baseline justify-between gap-2">
                                            <p className="min-w-0 truncate text-[15px] font-semibold text-black dark:text-white">
                                                {rma.item_title ||
                                                    (rma.sku ? `SKU ${rma.sku}` : `RMA ${rma.id.slice(-8)}`)}
                                            </p>
                                            <span className="shrink-0 text-[11px] text-mono-400 dark:text-mono-600">
                                                {days !== null ? `há ${days}d neste estágio` : timeAgo(rma.updated_at)}
                                            </span>
                                        </div>
                                        <p className="w-full truncate font-mono text-xs text-mono-500 dark:text-mono-400">
                                            {[
                                                rma.sku,
                                                rma.quantity > 1 ? `${rma.quantity} un` : null,
                                                rma.serial_number ? `série ${rma.serial_number}` : null,
                                                rma.external_order_id ? `pedido ${rma.external_order_id}` : null,
                                                rma.reason_detail || rma.reason_id,
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-mono-500 dark:text-mono-400">
                                            {rma.inspected_at && <span>inspecionado {fmtDate(rma.inspected_at)}</span>}
                                            {rma.supplier_registered_at && (
                                                <span>cadastrado {fmtDate(rma.supplier_registered_at)}</span>
                                            )}
                                            {rma.supplier_shipped_at && (
                                                <span>despachado {fmtDate(rma.supplier_shipped_at)}</span>
                                            )}
                                            <span
                                                className={
                                                    rma.supplier_nf_number
                                                        ? ''
                                                        : 'text-warning-text dark:text-warning-300'
                                                }
                                            >
                                                {rma.supplier_nf_number
                                                    ? `NF de devolução ${rma.supplier_nf_number}${rma.supplier_nf_at ? ` · ${fmtDate(rma.supplier_nf_at)}` : ''}`
                                                    : rma.supplier_shipped_at
                                                      ? 'sem NF de devolução registrada'
                                                      : null}
                                            </span>
                                            {rma.supplier_concluded_at && (
                                                <span className="flex items-center gap-1.5">
                                                    concluído {fmtDate(rma.supplier_concluded_at)}
                                                    <OutcomeChip outcome={rma.supplier_outcome} />
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 border-t border-mono-100 pt-2 dark:border-mono-900">
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/rmas/${rma.id}`)}
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-mono-500 underline underline-offset-2 hover:text-black dark:hover:text-white"
                                            >
                                                abrir ocorrência <ArrowSquareOut size={12} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {createOpen && (
                <CreateBatchModal
                    isOpen
                    saving={busy}
                    onClose={() => setCreateOpen(false)}
                    onConfirm={(data) => act(() => api.createSupplierBatch(data), 'RMA aberto — inclua os produtos.')}
                />
            )}
            {addItemsOpen && detailBatch && (
                <AddItemsModal
                    isOpen
                    saving={busy}
                    onClose={() => setAddItemsOpen(false)}
                    onConfirm={(rmaIds) =>
                        act(() => api.addSupplierBatchItems(detailBatch.id, rmaIds), 'Produtos incluídos.')
                    }
                />
            )}
            {shipOpen && detailBatch && (
                <ShipBatchModal
                    isOpen
                    batch={detailBatch}
                    saving={busy}
                    onClose={() => setShipOpen(false)}
                    onConfirm={({ nf_number, note }) =>
                        act(
                            () => api.shipSupplierBatch(detailBatch.id, { nf_number, note }),
                            'RMA despachado — aguardando o fabricante.',
                        )
                    }
                />
            )}
            {outcomeTarget && detailBatch && (
                <OutcomeModal
                    isOpen
                    saving={busy}
                    title={outcomeTarget.all ? 'Desfecho para todos os pendentes' : 'Desfecho do produto'}
                    description={
                        outcomeTarget.all
                            ? `Aplica o mesmo retorno aos ${pendingItems.length} produto(s) ainda sem desfecho.`
                            : 'Qual foi o retorno do fornecedor para este produto?'
                    }
                    onClose={() => setOutcomeTarget(null)}
                    onConfirm={({ outcome, note }) =>
                        act(
                            () =>
                                outcomeTarget.all
                                    ? api.concludeSupplierBatch(detailBatch.id, outcome, note)
                                    : api.concludeSupplierBatchItem(detailBatch.id, outcomeTarget.rmaId, outcome, note),
                            'Desfecho registrado.',
                        )
                    }
                />
            )}
        </div>
    );
};

export default Garantia;
